/**
 * use-last-package — localStorage hook for GH-252 upload package anchor.
 *
 * Key: `agentwiki:last-package:{tenantId}` (tenant-scoped to prevent cross-tenant leak).
 * Value: `{ packageId: string; sourceGroup: string }` JSON.
 *
 * Behaviour:
 *   - On mount: reads stored value and returns it (if present and valid).
 *   - `set(ctx)`: writes to localStorage + returns updated value.
 *   - `clear()`: removes the key from localStorage.
 */

import { useState, useEffect, useCallback } from 'react'
import type { SourceGroup } from '@agentwiki/shared'

export interface LastPackageContext {
  packageId: string
  sourceGroup: SourceGroup
}

function storageKey(tenantId: string): string {
  return `agentwiki:last-package:${tenantId}`
}

function readFromStorage(tenantId: string): LastPackageContext | null {
  try {
    const raw = localStorage.getItem(storageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'packageId' in parsed &&
      'sourceGroup' in parsed &&
      typeof (parsed as Record<string, unknown>).packageId === 'string' &&
      typeof (parsed as Record<string, unknown>).sourceGroup === 'string'
    ) {
      return parsed as LastPackageContext
    }
    return null
  } catch {
    return null
  }
}

export interface UseLastPackageReturn {
  /** Currently stored package context; null if none. */
  context: LastPackageContext | null
  /** Persist a new package context to localStorage. */
  set: (ctx: LastPackageContext) => void
  /** Remove the stored package context from localStorage. */
  clear: () => void
}

export function useLastPackage(tenantId: string | null): UseLastPackageReturn {
  const [context, setContext] = useState<LastPackageContext | null>(() => {
    if (!tenantId) return null
    return readFromStorage(tenantId)
  })

  // Re-read when tenantId changes (e.g. user switches tenant in same session)
  useEffect(() => {
    if (!tenantId) {
      setContext(null)
      return
    }
    setContext(readFromStorage(tenantId))
  }, [tenantId])

  const set = useCallback(
    (ctx: LastPackageContext) => {
      if (!tenantId) return
      localStorage.setItem(storageKey(tenantId), JSON.stringify(ctx))
      setContext(ctx)
    },
    [tenantId],
  )

  const clear = useCallback(() => {
    if (!tenantId) return
    localStorage.removeItem(storageKey(tenantId))
    setContext(null)
  }, [tenantId])

  return { context, set, clear }
}
